/** §5.1 Canonical serialization: keys sorted byte-wise, arrays in stored order,
 * integers only, `null` never `undefined`.
 *
 * Typed arrays serialize as a tagged object so a dense map hashes the same on
 * every runtime and survives structuredClone/JSON alike.
 */

/** Constructible from a length or from a list of values; the save codec needs
 *  the second form to rebuild an array it just parsed. */
export interface TypedArrayCtor {
  new (length: number): ArrayBufferView & ArrayLike<number>;
  new (values: readonly number[]): ArrayBufferView & ArrayLike<number>;
  readonly prototype: ArrayBufferView;
}

/**
 * The tag alphabet. It is exported because there must be exactly one answer to
 * "how does a dense map become text": the hash and the save file disagree the
 * moment there are two tables, and a save that hashes differently from the
 * state it came from is the whole determinism story lost (I12).
 *
 * Float32/Float64 are deliberately absent: a float in a kernel is a D4
 * violation, so it has no encoding and `canonicalize` rejects it below.
 */
export const TYPED_ARRAYS: ReadonlyArray<readonly [string, TypedArrayCtor]> = [
  ["i8", Int8Array], ["u8", Uint8Array], ["u8c", Uint8ClampedArray],
  ["i16", Int16Array], ["u16", Uint16Array],
  ["i32", Int32Array], ["u32", Uint32Array],
];

/** The tag a typed array serializes under, or null if `v` is not one. */
export function typedArrayTag(v: object): string | null {
  for (const [tag, ctor] of TYPED_ARRAYS) if (v instanceof ctor) return tag;
  return null;
}

/** The key a tagged typed array carries in the canonical form. */
export const TA_TAG_KEY = "__ta";
export const TA_VALUES_KEY = "v";

export function canonicalize(v: unknown): string {
  if (v === null) return "null";
  const t = typeof v;
  if (t === "number") {
    if (!Number.isFinite(v as number)) throw new Error(`non-finite number in state: ${String(v)}`);
    if (!Number.isInteger(v as number)) throw new Error(`float in canonical state (D4): ${String(v)}`);
    return String(v);
  }
  if (t === "boolean") return v ? "true" : "false";
  if (t === "string") return JSON.stringify(v);
  if (t === "undefined") throw new Error("undefined in state (I6): use null");
  if (t === "function" || t === "symbol" || t === "bigint") {
    throw new Error(`non-plain value in state (I6): ${t}`);
  }
  if (Array.isArray(v)) return `[${v.map(canonicalize).join(",")}]`;

  const tag = typedArrayTag(v as object);
  if (tag !== null) {
    const a = v as unknown as ArrayLike<number>;
    let s = `{${JSON.stringify(TA_TAG_KEY)}:${JSON.stringify(tag)},${JSON.stringify(TA_VALUES_KEY)}:[`;
    for (let i = 0; i < a.length; i++) { if (i) s += ","; s += String(a[i]); }
    return s + "]}";
  }

  const o = v as Record<string, unknown>;
  const keys = Object.keys(o).sort();
  let s = "{";
  let first = true;
  for (const k of keys) {
    const val = o[k];
    if (val === undefined) throw new Error(`undefined value at key "${k}" (I6)`);
    if (!first) s += ",";
    first = false;
    s += `${JSON.stringify(k)}:${canonicalize(val)}`;
  }
  return s + "}";
}
